import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { useCurrency } from '../../../context/currencyContext'; // Adjust path as needed

// Centralized styles
const styles = {
  pageContainer: {
    backgroundColor: '#f5f7fa',
    minHeight: '100vh',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  sidebar: {
    borderRight: '1px solid #e5e5e5',
    minHeight: 'calc(100vh - 70px)',
    padding: '20px 0',
    backgroundColor: '#ffffff',
  },
  sidebarTitle: {
    margin: '15px 0 20px 30px',
    color: '#004aad',
    fontSize: '22px',
    fontWeight: '600',
  },
  sidebarLink: {
    textDecoration: 'none',
    color: '#0056b3',
    fontWeight: '500',
    fontSize: '16px',
    display: 'block',
    margin: '0 30px 15px',
    padding: '8px 12px',
    borderRadius: '4px',
    transition: 'background-color 0.2s ease, color 0.2s ease',
  },
  contentWrapper: {
    padding: '30px',
  },
  contentBox: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    padding: '30px',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
    paddingBottom: '15px',
    borderBottom: '2px solid #004aad',
  },
  invoiceHeading: {
    fontSize: '24px',
    color: '#262626',
    fontWeight: '600',
  },
  caseLink: {
    color: '#004aad',
    textDecoration: 'none',
    fontWeight: '600',
    transition: 'color 0.2s ease',
  },
  actionButtonContainer: {
    position: 'relative',
  },
  actionButton: {
    padding: '10px 20px',
    backgroundColor: '#004aad',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '14px',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0,74,173,0.2)',
    transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    backgroundColor: '#fff',
    border: '1px solid #e5e5e5',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    zIndex: 1000,
    width: '180px',
    overflow: 'hidden',
  },
  dropdownItem: {
    padding: '10px 15px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#333',
    borderBottom: '1px solid #f0f0f0',
    transition: 'background-color 0.2s ease',
  },
  detailsGrid: {
    marginBottom: '30px',
  },
  infoBlock: {
    padding: '20px',
    border: '1px solid #e5e5e5',
    borderRadius: '6px',
    backgroundColor: '#fdfdfd',
  },
  infoBlockTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#004aad',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px solid #e5e5e5',
  },
  detailItem: {
    marginBottom: '8px',
    fontSize: '13px',
    lineHeight: '1.6',
  },
  detailLabel: {
    fontWeight: '600',
    color: '#444',
    display: 'inline-block',
    minWidth: '100px',
    verticalAlign: 'top',
  },
  detailValue: {
    color: '#555',
  },
  itemsSection: {
    marginBottom: '25px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#004aad',
    marginBottom: '15px',
    paddingBottom: '10px',
    borderBottom: '1px solid #e5e5e5',
  },
  itemsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px',
  },
  tableHeader: {
    backgroundColor: '#eaf0ff',
    color: '#004aad',
    fontWeight: 'bold',
  },
  tableCell: {
    border: '1px solid #ddd',
    padding: '10px',
    textAlign: 'left',
  },
  textRight: {
    textAlign: 'right',
  },
  tableRowEven: {
    backgroundColor: '#f8f9fa',
  },
  noItemsRow: {
    textAlign: 'center',
    color: '#777',
    padding: '15px',
  },
  summarySection: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '25px',
  },
  summaryTable: {
    width: '45%',
    maxWidth: '350px',
    fontSize: '13px',
  },
  summaryRow: {
    padding: '8px 12px',
  },
  summaryLabel: {
    textAlign: 'right',
    fontWeight: '600',
    color: '#444',
  },
  summaryValue: {
    textAlign: 'right',
    color: '#333',
  },
  totalRow: {
    fontWeight: 'bold',
    color: '#004aad',
    fontSize: '15px',
    borderTop: '2px solid #004aad',
    paddingTop: '10px',
    backgroundColor: '#eaf0ff',
  },
  footerSection: {
    textAlign: 'center',
    marginTop: '40px',
    paddingTop: '20px',
    borderTop: '1px solid #ccc',
    fontSize: '11px',
    color: '#666',
  },
  thankYou: {
    color: '#4CAF50',
    fontStyle: 'italic',
    display: 'block',
    marginTop: '8px',
    fontSize: '12px',
  },
  loadingError: {
    textAlign: 'center',
    marginTop: '60px',
    fontSize: '18px',
    padding: '20px',
    color: '#d9534f',
    backgroundColor: '#fff',
    borderRadius: '8px',
  },
  headerSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '25px',
    paddingBottom: '15px',
    borderBottom: '2px solid #004aad',
  },
  clinicInfo: {
    fontSize: '12px',
    color: '#333',
    lineHeight: '1.4',
  },
  currencyInfo: {
    backgroundColor: '#e9ecef',
    padding: '8px 12px',
    borderRadius: '4px',
    marginBottom: '15px',
    fontSize: '14px',
    border: '1px solid #dee2e6',
    display: 'inline-block'
  },
};

// CSS for page transitions
const transitionStyles = `
  .fade-enter {
    opacity: 0;
  }
  .fade-enter-active {
    opacity: 1;
    transition: opacity 300ms ease-in;
  }
  .fade-exit {
    opacity: 1;
  }
  .fade-exit-active {
    opacity: 0;
    transition: opacity 300ms ease-out;
  }
`;

const InvoiceDetail = () => {
  const { formatAmount } = useCurrency(); // Get formatAmount from currency context
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { invoiceData: initialInvoiceData } = location.state || {};
  const [invoiceData, setInvoiceData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const { invoiceNumber } = useParams();
  const [visiblePages, setVisiblePages] = useState([]);
  const [userId, setUserId] = useState(null);

  // Constants moved inside component
  const Account_Name = invoiceData?.accountHolder || 'HIRA DENTAL LAB';
  const Account_Address_footer = invoiceData?.account_address || '';
  const Account_Email = invoiceData?.account_email || 'contact@hiradentallab.com';
  const Account_Phone = invoiceData?.account_phone || '(123) 456-7890';

  // Inject transition styles
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = transitionStyles;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  // Fetch userId from localStorage
  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    setUserId(storedUserId);
  }, []);

  // Fetch user permissions if userId is not 1
  useEffect(() => {
    if (userId && userId !== '1') {
      const fetchPermissions = async () => {
        try {
          const response = await fetch(`https://hms.backend.hiragroupcloud.com/api/page/user/${userId}/pages`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          });
          if (!response.ok) {
            throw new Error('Failed to fetch permissions');
          }
          const data = await response.json();
          setVisiblePages(data.map(perm => perm.page.name));
        } catch (error) {
          console.error('Error fetching permissions:', error);
        }
      };
      fetchPermissions();
    }
  }, [userId]);

  // Check if initialInvoiceData is complete
  const isInvoiceDataComplete = (data) => {
    return (
      data &&
      data.invoiceNumber &&
      data.total != null &&
      data.subtotal != null &&
      data.taxes != null &&
      data.paid != null &&
      Array.isArray(data.items)
    );
  };

  // Fetch invoice data
  useEffect(() => {
    const fetchInvoiceData = async () => {
      try {
        // If initialInvoiceData is complete and matches invoiceNumber, use it
        if (
          initialInvoiceData &&
          String(initialInvoiceData.invoiceNumber) === invoiceNumber &&
          isInvoiceDataComplete(initialInvoiceData)
        ) {
          setInvoiceData({
            ...initialInvoiceData,
            remaining: (parseFloat(initialInvoiceData.total) - parseFloat(initialInvoiceData.paid || 0)).toFixed(2),
          });
          setLoading(false);
          return;
        }

        // Otherwise, fetch from API
        setLoading(true);
        const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/invoices/${invoiceNumber}`);
        if (response.data) {
          setInvoiceData({
            ...response.data,
            remaining: (parseFloat(response.data.total) - parseFloat(response.data.paid || 0)).toFixed(2),
          });
        } else {
          throw new Error('Invoice not found');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching invoice data:', err);
        setError('Failed to load invoice data: ' + (err.response?.data?.message || err.message));
        setLoading(false);
      }
    };

    if (invoiceNumber) {
      fetchInvoiceData();
    } else {
      setError('Invoice number is missing.');
      setLoading(false);
    }
  }, [invoiceNumber, initialInvoiceData]);

  // Formatting utilities
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Use formatAmount from currency context instead of hardcoded £
  const formatCurrency = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? formatAmount(0) : formatAmount(num);
  };

  const calculateRate = (amount, quantity) => {
    const amountNum = parseFloat(amount) || 0;
    const quantityNum = parseFloat(quantity) || 1;
    return quantityNum === 0 ? 0 : amountNum / quantityNum;
  };

  // Delete invoice handler
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
      return;
    }
    try {
      setLoading(true);
      await axios.delete(`https://hms.backend.hiragroupcloud.com/api/invoices/delete-invoice/${invoiceNumber}`);
      alert('Invoice deleted successfully.');
      navigate('/casesOpened'); // Redirect to caseOpened page
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError('Failed to delete invoice: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  // Print functionality
  const handlePrint = () => {
    const htmlContent = generateInvoiceHTML();
    if (!htmlContent) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  // PDF generation
  const downloadPDF = (fileName) => {
    if (!invoiceData) {
      alert('Cannot generate PDF: Invoice data is missing.');
      return;
    }

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = { top: 15, right: 15, bottom: 20, left: 15 };
      const usableWidth = pageWidth - margin.left - margin.right;
      let currentY = margin.top;

      const colors = {
        primary: '#004aad',
        secondary: '#eaf0ff',
        accent: '#4CAF50',
        text: '#333333',
        lightText: '#666666',
        border: '#dddddd',
      };

      const fonts = {
        xlarge: 18,
        large: 14,
        medium: 11,
        normal: 10,
        small: 8,
      };

      const lineHeights = {
        large: 7,
        normal: 5,
        small: 4,
      };

      // Helper function to add text with options
      const addText = (text, x, y, options = {}) => {
        pdf.setFontSize(options.fontSize || fonts.normal);
        pdf.setFont('helvetica', options.style || 'normal');
        pdf.setTextColor(options.color || colors.text);
        const textToPrint = String(text ?? '');
        const textLines = pdf.splitTextToSize(textToPrint, options.maxWidth || usableWidth);
        pdf.text(textLines, x, y, { align: options.align || 'left' });
        return y + textLines.length * (options.lineHeight || lineHeights.normal);
      };

      // Header Section
      addText(Account_Name, margin.left, currentY, {
        fontSize: fonts.xlarge,
        style: 'bold',
        color: colors.primary,
        lineHeight: lineHeights.large + 2,
      });

      let headerRightY = currentY;
      headerRightY = addText(
        `Invoice #${invoiceData.invoiceNumber || 'N/A'}`,
        pageWidth - margin.right,
        headerRightY,
        { align: 'right', fontSize: fonts.large, style: 'bold', lineHeight: lineHeights.large }
      );

      addText(
        `Generated: ${new Date().toLocaleDateString('en-GB')}`,
        pageWidth - margin.right,
        headerRightY,
        { align: 'right', fontSize: fonts.small, color: colors.lightText, lineHeight: lineHeights.small }
      );

      currentY += lineHeights.large + 2;

      // Clinic Info Section
      addText(Account_Address_footer, margin.left, currentY, {
        fontSize: fonts.small,
        color: colors.text,
        lineHeight: lineHeights.small
      });

      addText(`Phone: ${Account_Phone}`, margin.left, currentY + lineHeights.small, {
        fontSize: fonts.small,
        color: colors.text,
        lineHeight: lineHeights.small
      });

      addText(`Email: ${Account_Email}`, margin.left, currentY + (lineHeights.small * 2), {
        fontSize: fonts.small,
        color: colors.text,
        lineHeight: lineHeights.small
      });

      currentY += lineHeights.normal * 3;

      // Invoice Details Section
      const addDetail = (label, value) => {
        addText(label, margin.left, currentY, {
          style: 'bold',
          color: colors.primary,
          fontSize: fonts.normal,
          lineHeight: lineHeights.normal,
        });

        addText(value, margin.left + 40, currentY, {
          fontSize: fonts.normal,
          lineHeight: lineHeights.normal,
        });

        currentY += lineHeights.normal;
      };

      addDetail('Invoice Date:', formatDate(invoiceData.invoiceDate));
      addDetail('Patient:', invoiceData.patient_name || 'N/A');
      addDetail('CaseId:', invoiceData.caseId || 'N/A');
      addDetail('Shipper:', invoiceData.shipper || 'N/A');
      addDetail('Tracking #:', invoiceData.trackNumber || 'N/A');

      currentY += lineHeights.normal;

      // Items Table
      pdf.setFillColor(colors.secondary);
      pdf.rect(margin.left, currentY, usableWidth, lineHeights.large, 'F');
      pdf.setTextColor(colors.primary);
      pdf.setFontSize(fonts.normal);
      pdf.setFont('helvetica', 'bold');

      const colWidths = {
        desc: usableWidth * 0.48,
        qty: usableWidth * 0.12,
        rate: usableWidth * 0.20,
        amount: usableWidth * 0.20,
      };

      // Table Headers
      let currentX = margin.left + 3;
      pdf.text('Description', currentX, currentY + lineHeights.large - 2);
      currentX += colWidths.desc;
      pdf.text('Qty', currentX + colWidths.qty - 3, currentY + lineHeights.large - 2, { align: 'right' });
      currentX += colWidths.qty;
      pdf.text('Rate', currentX + colWidths.rate - 3, currentY + lineHeights.large - 2, { align: 'right' });
      currentX += colWidths.rate;
      pdf.text('Amount', currentX + colWidths.amount - 3, currentY + lineHeights.large - 2, { align: 'right' });

      currentY += lineHeights.large;

      // Table Rows
      pdf.setFont('helvetica', 'normal');
      pdf.setDrawColor(colors.border);
      pdf.setLineWidth(0.2);

      (invoiceData.items || []).forEach((item, index) => {
        const descriptionText = item.description || 'N/A';
        const qtyText = String(item.quantity || '0');
        const rateText = formatCurrency(calculateRate(item.amount, item.quantity));
        const amountText = formatCurrency(item.amount);

        const rowHeight = lineHeights.normal + 4;

        if (index % 2 !== 0) {
          pdf.setFillColor('#f9f9f9');
          pdf.rect(margin.left, currentY, usableWidth, rowHeight, 'F');
        }

        let cellX = margin.left + 3;
        addText(descriptionText, cellX, currentY + lineHeights.normal + 1, {
          maxWidth: colWidths.desc - 6,
          fontSize: fonts.normal,
          lineHeight: lineHeights.normal,
          color: colors.text,
        });

        cellX += colWidths.desc;
        addText(qtyText, cellX + colWidths.qty - 3, currentY + lineHeights.normal + 1, {
          align: 'right',
          maxWidth: colWidths.qty - 6,
          fontSize: fonts.normal,
          lineHeight: lineHeights.normal,
          color: colors.text,
        });

        cellX += colWidths.qty;
        addText(rateText, cellX + colWidths.rate - 3, currentY + lineHeights.normal + 1, {
          align: 'right',
          maxWidth: colWidths.rate - 6,
          fontSize: fonts.normal,
          lineHeight: lineHeights.normal,
          color: colors.text,
        });

        cellX += colWidths.rate;
        addText(amountText, cellX + colWidths.amount - 3, currentY + lineHeights.normal + 1, {
          align: 'right',
          maxWidth: colWidths.amount - 6,
          fontSize: fonts.normal,
          lineHeight: lineHeights.normal,
          color: colors.text,
        });

        currentY += rowHeight;
        pdf.line(margin.left, currentY, margin.left + usableWidth, currentY);
      });

      if (!invoiceData.items || invoiceData.items.length === 0) {
        addText('No items in this invoice.', margin.left + usableWidth / 2, currentY + lineHeights.normal, {
          align: 'center',
          color: colors.lightText,
          fontSize: fonts.normal,
          lineHeight: lineHeights.normal,
        });
        currentY += lineHeights.normal * 2;
      }

      // Summary Section
      currentY += lineHeights.normal * 1.5;

      const addSummaryLine = (label, value) => {
        addText(label, margin.left + usableWidth * 0.6, currentY, {
          align: 'left',
          fontSize: fonts.normal,
          lineHeight: lineHeights.normal,
        });

        addText(value, margin.left + usableWidth, currentY, {
          align: 'right',
          fontSize: fonts.normal,
          lineHeight: lineHeights.normal,
        });

        currentY += lineHeights.normal;
      };

      addSummaryLine('Subtotal:', formatCurrency(invoiceData.subtotal));
      addSummaryLine('Taxes:', formatCurrency(invoiceData.taxes));
      addSummaryLine('Total Due:', formatCurrency(invoiceData.total));
      addSummaryLine('Amount Paid:', formatCurrency(invoiceData.paid || 0));

      currentY += 1;
      pdf.setDrawColor(colors.primary);
      pdf.setLineWidth(0.4);

      addSummaryLine('Remaining Balance:', formatCurrency(invoiceData.remaining || 0));

      // Footer
      const footerText = `02 Ulverscroft Rd, Coventry, UK - Phone: +44 24 7626 3962 - Email: info@hiradentallab.com`;

      pdf.setDrawColor(colors.primary);
      pdf.setLineWidth(0.2);
      pdf.line(margin.left, pageHeight - margin.bottom + 5, pageWidth - margin.right, pageHeight - margin.bottom + 5);

      addText(
        footerText,
        pageWidth / 2,
        pageHeight - margin.bottom + 10,
        { align: 'center', fontSize: fonts.small, color: colors.lightText, lineHeight: lineHeights.small }
      );

      addText(
        'Thank you for your business!',
        pageWidth / 2,
        pageHeight - margin.bottom + 15,
        { align: 'center', fontSize: fonts.small, color: colors.accent, style: 'italic', lineHeight: lineHeights.small }
      );

      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('An error occurred while generating the PDF.');
    }
  };

  // Generate HTML for print
  const generateInvoiceHTML = () => {
    if (!invoiceData) {
      console.error('Cannot generate HTML for Print: Invoice data is missing.');
      alert('Cannot generate HTML for Print: Invoice data is missing.');
      return null;
    }
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${invoiceData.invoiceNumber || 'N/A'}</title>
        <style>
          @page { size: A4; margin: 20mm; }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.5;
            color: #333;
            margin: 0;
            padding: 0;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          .invoice-container {
            padding: 0;
            width: 100%;
            box-sizing: border-box;
            margin: auto;
          }
          .header-section {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 25px;
            padding-bottom: 15px;
            border-bottom: 2px solid #004aad;
          }
          .clinic-info .clinic-name {
            font-size: 26px;
            color: #004aad;
            font-weight: 600;
            margin-bottom: 5px;
          }
          .clinic-info .clinic-address {
            font-size: 12px;
            color: #555;
            line-height: 1.4;
          }
          .invoice-meta {
            text-align: right;
          }
          .invoice-meta .invoice-title {
            font-size: 22px;
            font-weight: 600;
            color: #333;
            margin-bottom: 8px;
          }
          .invoice-meta .meta-item {
            font-size: 12px;
            color: #555;
            margin-bottom: 4px;
          }
          .invoice-meta .meta-item strong {
            color: #444;
          }
          .details-grid {
            margin-bottom: 30px;
          }
          .info-block {
            padding: 15px;
            border: 1px solid #e0e0e0;
            border-radius: 6px;
            background-color: #fdfdfd;
            max-width: 350px;
          }
          .info-block .block-title {
            font-size: 16px;
            font-weight: 600;
            color: #004aad;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 1px solid #e5e5e5;
          }
          .info-block .detail-item {
            margin-bottom: 8px;
            font-size: 13px;
            line-height: 1.6;
          }
          .info-block .detail-item strong {
            font-weight: 600;
            color: #444;
            display: inline-block;
            min-width: 90px;
            verticalAlign: top;
          }
          .items-section .section-title {
            font-size: 18px;
            font-weight: 600;
            color: #004aad;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 1px solid #e0e0e0;
          }
          table.items-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            font-size: 12px;
          }
          .items-table th,
          .items-table td {
            border: 1px solid #ddd;
            padding: 10px;
            text-align: left;
          }
          .items-table th {
            background-color: #eaf0ff';
            color: '#004aad';
            font-weight: 'bold',
          }
          .items-table .text-right {
            text-align: right;
          }
          .items-table tbody tr:nth-child(even) {
            background-color: #f8f9fa;
          }
          .items-table .no-items-row td {
            text-align: center;
            color: #777;
            padding: 15px;
          }
          .summary-section {
            margin-top: 25px;
            display: flex;
            justify-content: flex-end;
          }
          .summary-table {
            width: 45%;
            max-width: 350px;
            font-size: 13px;
          }
          .summary-table td {
            padding: 8px 12px;
          }
          .summary-table tr td:first-child {
            text-align: right;
            font-weight: 600;
            color: #444;
          }
          .summary-table tr td:last-child {
            text-align: right;
            color: #333;
          }
          .summary-table tr.total-row td {
            font-weight: bold;
            color: #004aad';
            font-size: '15px',
            borderTop: '2px solid #004aad',
            paddingTop: '10px',
            backgroundColor: '#eaf0ff',
          }
          .footer-section {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ccc;
            font-size: 11px;
            color: #666;
          }
          .footer-section .thank-you {
            color: #4CAF50;
            font-style: italic;
            display: block;
            margin-top: 8px;
            font-size: 12px;
          }
          @media print {
            .invoice-container {
              width: 100%;
              margin: 0;
            }
            .info-block {
              max-width: 100%;
            }
            .summary-table {
              width: 40%;
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="header-section">
            <div class="clinic-info">
  <div class="clinic-name">${Account_Name}</div>
  <div class="clinic-address">${Account_Address_footer}</div>
  <div class="clinic-address">Phone: ${Account_Phone}</div>
  <div class="clinic-address">Email: ${Account_Email}</div>
</div>
            <div class="invoice-meta">
              <div class="invoice-title">Invoice #${invoiceData.invoiceNumber || 'N/A'}</div>
              <div class="meta-item"><strong>Generated:</strong> ${new Date().toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}</div>
              <div class="meta-item"><strong>Case #:</strong> ${invoiceData.caseId || 'N/A'}</div>
            </div>
          </div>

          <div class="details-grid">
            <div class="info-block">
              <div class="block-title">Invoice Details</div>
              <div class="detail-item"><strong>Invoice Date:</strong> <span>${formatDate(invoiceData.invoiceDate)}</span></div>
              <div class="detail-item"><strong>Patient:</strong> <span>${invoiceData.patient_name || 'N/A'}</span></div>
              <div class="detail-item"><strong>Shipper:</strong> <span>${invoiceData.shipper || 'N/A'}</span></div>
              <div class="detail-item"><strong>Tracking Ref#:</strong> <span>${invoiceData.trackNumber || 'N/A'}</span></div>
            </div>
            
          </div>
          <div class="items-section">
            <div class="section-title">Items</div>
            <table class="items-table">
              <thead>
                <tr><th>Description</th><th class="text-right">Qty</th><th class="text-right">Rate</th><th class="text-right">Amount</th></tr>
              </thead>
              <tbody>
                ${(invoiceData.items || []).map(
                  (item) => `
                  <tr>
                    <td>${item.description || 'N/A'}</td>
                    <td class="text-right">${item.quantity || '0'}</td>
                    <td class="text-right">${formatCurrency(calculateRate(item.amount, item.quantity))}</td>
                    <td class="text-right">${formatCurrency(item.amount)}</td>
                  </tr>
                `
                ).join('')}
                ${(invoiceData.items && invoiceData.items.length > 0) ? '' : `<tr class="no-items-row"><td colspan="4">No items in this invoice.</td></tr>`}
              </tbody>
            </table>
          </div>

          <div class="summary-section">
            <table class="summary-table">
              <tr><td>Subtotal:</td><td>${formatCurrency(invoiceData.subtotal)}</td></tr>
              <tr><td>Taxes:</td><td>${formatCurrency(invoiceData.taxes)}</td></tr>
              <tr><td>Total Due:</td><td>${formatCurrency(invoiceData.total || 0)}</td></tr>
              <tr><td>Amount Paid:</td><td>${formatCurrency(invoiceData.paid || 0)}</td></tr>
              <tr class="total-row"><td>Remaining Balance:</td><td>${formatCurrency(invoiceData.remaining)}</td></tr>
            </table>
          </div>

          <div class="footer-section">
            02 Ulverscroft Rd, Coventry, UK - Phone: +44 24 7626 3962 - Email: info@hiradentallab.com
            <span class="thank-you">Thank you for your business!</span>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Dropdown actions
  const dropdownOptions = [
    { label: 'Print', action: 'Print' },
    { label: 'Save as PDF', action: 'Save Classic PDF' },
    ...(userId === '1' || visiblePages.includes('Edit Invoice') ? [{ label: 'Edit', action: 'Edit' }] : []),
    { label: 'Delete', action: 'Delete' },
  ];

  const handleOptionClick = (option) => {
    setDropdownOpen(false);
    if (option === 'Print') {
      handlePrint();
    } else if (option === 'Save Classic PDF') {
      downloadPDF(`Invoice-${invoiceData.invoiceNumber || invoiceData.caseId}-Classic.pdf`);
    } else if (option === 'Edit') {
      navigate(`/invoices/${invoiceNumber}/edit`, { state: { invoiceData } });
    } else if (option === 'Delete') {
      handleDelete();
    }
  };

  // Render loading or error states
  if (loading) {
    return <div style={styles.loadingError}>Loading invoice...</div>;
  }
  if (error) {
    return <div style={styles.loadingError}>{error}</div>;
  }
  if (!invoiceData) {
    return <div style={styles.loadingError}>Invoice data not available. Please check the invoice number.</div>;
  }

  return (
    <>
   
      <TransitionGroup>
        <CSSTransition key={location.key} classNames="fade" timeout={300}>
          <div style={styles.pageContainer}>
            <div className="row g-0">
              <div className="col-md-2" style={styles.sidebar}>
                <h2 style={styles.sidebarTitle}>Finances</h2>
                <div>
                  <Link
                    to="/postings"
                    style={styles.sidebarLink}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#eaf0ff';
                      e.currentTarget.style.color = '#003a8c';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#0056b3';
                    }}
                  >
                    Postings
                  </Link>
                  <Link
                    to="/Statements"
                    style={styles.sidebarLink}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#eaf0ff';
                      e.currentTarget.style.color = '#003a8c';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#0056b3';
                    }}
                  >
                    Statements
                  </Link>
                </div>
              </div>
              <div className="col-md-10" style={styles.contentWrapper}>
                <div style={styles.contentBox}>
                  <div style={styles.headerContainer}>
                    <div style={styles.invoiceHeading}>
                      Invoice #{invoiceData.invoiceNumber || 'N/A'} / Case{' '}
                      <Link
                        to={`/case/${invoiceData.caseId}`}
                        style={styles.caseLink}
                        onMouseEnter={(e) => (e.currentTarget.style.color = '#003688')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = '#004aad')}
                      >
                        #{invoiceData.caseId || 'N/A'}
                      </Link>
                    </div>
                    <div style={styles.actionButtonContainer}>
                      <button
                        type="button"
                        aria-haspopup="true"
                        aria-expanded={isDropdownOpen}
                        style={styles.actionButton}
                        onClick={() => setDropdownOpen(!isDropdownOpen)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#003a8c';
                          e.currentTarget.style.boxShadow = '0 3px 6px rgba(0,58,140,0.25)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#004aad';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,74,173,0.2)';
                        }}
                      >
                        Actions ▾
                      </button>
                      {isDropdownOpen && (
                        <div style={styles.dropdown}>
                          {dropdownOptions.map(({ label, action }, index) => (
                            <div
                              key={label}
                              style={{
                                ...styles.dropdownItem,
                                ...(index === dropdownOptions.length - 1 ? { borderBottom: 'none' } : {}),
                                ...(action === 'Delete' ? { color: '#d9534f' } : {}), // Red color for delete option
                              }}
                              onClick={() => handleOptionClick(action)}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f5ff')}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fff')}
                            >
                              {label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={styles.headerSection}>
                    <div style={styles.clinicInfo}>
  <div style={{ fontSize: '26px', color: '#004aad', fontWeight: '600', marginBottom: '5px' }}>
    {Account_Name}
  </div>
  <div>
    {Account_Address_footer.split(' - ').map((line, index) => (
      <div key={index} style={{ fontSize: '12px', color: '#555', lineHeight: '1.4' }}>
        {line}
      </div>
    ))}
    {/* Add email and phone */}
    <div style={{ fontSize: '12px', color: '#555', lineHeight: '1.4' }}>
      Phone: {Account_Phone}
    </div>
    <div style={{ fontSize: '12px', color: '#555', lineHeight: '1.4' }}>
      Email: {Account_Email}
    </div>
  </div>
</div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '22px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>
                        Invoice #{invoiceData.invoiceNumber || 'N/A'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#555', marginBottom: '4px' }}>
                        <strong style={{ color: '#444' }}>Generated:</strong>{' '}
                        {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </div>
                      <div style={{ fontSize: '12px', color: '#555' }}>
                        <strong style={{ color: '#444' }}>Case #:</strong> {invoiceData.caseId || 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div style={styles.detailsGrid}>
                    <div style={styles.infoBlock}>
                      <div style={styles.infoBlockTitle}>Invoice Details</div>
                      <div style={styles.detailItem}>
                        <strong style={styles.detailLabel}>Invoice Date:</strong>
                        <span style={styles.detailValue}>{formatDate(invoiceData.invoiceDate)}</span>
                      </div>
                      <div style={styles.detailItem}>
                        <strong style={styles.detailLabel}>Patient:</strong>
                        <span style={styles.detailValue}>{invoiceData.patient_name || 'N/A'}</span>
                      </div>
                      <div style={styles.detailItem}>
                        <strong style={styles.detailLabel}>Shipper:</strong>
                        <span style={styles.detailValue}>{invoiceData.shipper || 'N/A'}</span>
                      </div>
                      <div style={styles.detailItem}>
                        <strong style={styles.detailLabel}>Tracking #:</strong>
                        <span style={styles.detailValue}>{invoiceData.trackNumber || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  <div style={styles.itemsSection}>
                    <div style={styles.sectionTitle}>Items</div>
                    <table style={styles.itemsTable}>
                      <thead>
                        <tr style={styles.tableHeader}>
                          <th style={{ ...styles.tableCell, width: '48%' }}>Description</th>
                          <th style={{ ...styles.tableCell, ...styles.textRight, width: '12%' }}>Qty</th>
                          <th style={{ ...styles.tableCell, ...styles.textRight, width: '20%' }}>Rate</th>
                          <th style={{ ...styles.tableCell, ...styles.textRight, width: '20%' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(invoiceData.items || []).map((item, index) => (
                          <tr key={item._id || index} style={index % 2 === 0 ? {} : styles.tableRowEven}>
                            <td style={styles.tableCell}>{item.description || 'N/A'}</td>
                            <td style={{ ...styles.tableCell, ...styles.textRight }}>{item.quantity || '0'}</td>
                            <td style={{ ...styles.tableCell, ...styles.textRight }}>
                              {formatCurrency(calculateRate(item.amount, item.quantity))}
                            </td>
                            <td style={{ ...styles.tableCell, ...styles.textRight }}>{formatCurrency(item.amount)}</td>
                          </tr>
                        ))}
                        {(!invoiceData.items || invoiceData.items.length === 0) && (
                          <tr style={styles.noItemsRow}>
                            <td colSpan="4" style={styles.tableCell}>
                              No items in this invoice.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div style={styles.summarySection}>
                    <table style={styles.summaryTable}>
                      <tr style={styles.summaryRow}>
                        <td style={styles.summaryLabel}>Subtotal:</td>
                        <td style={styles.summaryValue}>{formatCurrency(invoiceData.subtotal)}</td>
                      </tr>
                      <tr style={styles.summaryRow}>
                        <td style={styles.summaryLabel}>Taxes:</td>
                        <td style={styles.summaryValue}>{formatCurrency(invoiceData.taxes)}</td>
                      </tr>
                      <tr style={styles.summaryRow}>
                        <td style={styles.summaryLabel}>Total Due:</td>
                        <td style={styles.summaryValue}>{formatCurrency(invoiceData.total || 0)}</td>
                      </tr>
                      <tr style={styles.summaryRow}>
                        <td style={styles.summaryLabel}>Amount Paid:</td>
                        <td style={styles.summaryValue}>{formatCurrency(invoiceData.paid || 0)}</td>
                      </tr>
                      <tr style={styles.totalRow}>
                        <td style={styles.summaryLabel}>Remaining Balance:</td>
                        <td style={styles.summaryValue}>{formatCurrency(invoiceData.remaining)}</td>
                      </tr>
                    </table>
                  </div>
                  <div style={styles.footerSection}>
                    02 Ulverscroft Rd, Coventry, UK - Phone: +44 24 7626 3962 - Email:  info@hiradentallab.com
                    <span style={styles.thankYou}>Thank you for your business!</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CSSTransition>
      </TransitionGroup>
    </>
  );
};

export default InvoiceDetail;